import { isAfter } from 'date-fns';
import { useContext, useEffect, useRef, useState } from 'react';
import get from 'lodash/get';
import Router, { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { DomainContext } from 'contexts/domain/DomainContext';
import { useLinkResolver } from 'prismic/helpers/useLinkResolver';
import { formatDateForQuery } from 'helpers/date';
import { BlogNewsCategoriesObj } from 'pages/api/blogApi.types';
import { cleanRouterQuery, mapObjectToQueryString } from 'helpers/url';
import { SortGroupStyled, SortGroupWithSpaceStyled } from 'components/molecules/FilterRows';
import { mapEdgeItemToNewsBoxItem } from 'components/molecules/NewsSliderItem/NewsSliderItem.helper';
import { NewsSliderItemProps } from 'components/molecules/NewsSliderItem/NewsSliderItem.types';
import ComponentsRenderer from 'components/templates/partials/ComponentsRenderer';
import { CtaDropdown } from 'components/molecules/CtaDropdown';
import { Separator } from 'components/organisms/Separator';
import Container from 'components/layout/Grid/Container';
import { Loading } from 'components/atoms/Loading';
import { BreakpointContext } from 'contexts/breakpoint/BreakpointContext';
import Link from 'components/atoms/Link/Link';
import { SpacerStyled, RowSortStyled, ResultsCounterStyled } from 'components/molecules/FilterRows';
import { BlogOverviewProps } from './BlogOverview.types';
import { BlogOverviewContentHeader } from './BlogOverviewContentHeader.partial';
import {
  ColumnStyled,
  RowFilterStyled,
  FilterGroupStyled,
  RowGridStyled,
  RowLoadingStyled,
  RowPaginationStyled,
  DatePickerStyled,
  NewsSliderItemStyled,
  RowFilterHeadlineStyled,
  DatePickersGroupStyled,
  FilterGroupWithSpaceStyled,
} from './BlogOverview.styles';

export const BlogOverview: React.FC<BlogOverviewProps> = ({ data, blogs }) => {
  const router = useRouter();
  const bp = useContext(BreakpointContext);
  const { t, lang } = useTranslation('common');
  const { locale } = useLinkResolver();

  const [isloading, setIsLoading] = useState(false);

  const {
    computed: { isCourse },
  } = useContext(DomainContext);

  const isMobile = bp === 'xs' || bp === 'sm';
  const gridItems = 6;

  let blogItems: NewsSliderItemProps[] = [];

  if (blogs) {
    blogItems = blogs.map((item) => mapEdgeItemToNewsBoxItem(locale, item));
  }

  const mapStartDate = (): Date | undefined => {
    const queryObj = cleanRouterQuery(router.query);
    const queryDate = queryObj.startDate;
    const singleQueryDate = Array.isArray(queryDate) ? queryDate[0] : queryDate;
    if (singleQueryDate) {
      return queryDate ? new Date(singleQueryDate) : new Date();
    }
    return new Date();
  };

  const mapEndDate = (): Date | undefined => {
    const queryObj = cleanRouterQuery(router.query);
    const queryDate = queryObj.endDate;
    const singleQueryDate = Array.isArray(queryDate) ? queryDate[0] : queryDate;
    if (singleQueryDate) {
      return queryDate ? new Date(singleQueryDate) : new Date();
    }
    return new Date();
  };

  const [cursor, setCursor] = useState<number>(gridItems);
  const [startDate, setStartDate] = useState(mapStartDate());
  const [endDate, setEndDate] = useState(mapEndDate());

  // Change url with dates in query  on change datepickers
  const updateQueryDates = (): void => {
    const queryObj = cleanRouterQuery(router.query);
    if (!startDate || !endDate) {
      return;
    }
    queryObj.startDate = formatDateForQuery(startDate);
    queryObj.endDate = formatDateForQuery(endDate);
    const queryString = mapObjectToQueryString(queryObj);
    setCursor(gridItems);
    router.push(`/${lang}${router.asPath.split('?')[0]}${queryString}`, undefined);
  };

  // Avoid double call by dates States on initial Load
  function useDidUpdateEffect(fn: any, inputs: any): void {
    const didMountRef = useRef(false);

    useEffect(() => {
      if (didMountRef.current) {
        return fn();
      }
      didMountRef.current = true;
    }, inputs);
  }

  useDidUpdateEffect(updateQueryDates, [startDate, endDate]);

  const onEndDateChange = (date: Date | null) => {
    if (!date) {
      return;
    }
    setEndDate(date);
  };

  const onStartDateChange = (date: Date | null) => {
    if (!date) {
      return;
    }

    if (date && endDate && isAfter(date, endDate)) {
      setEndDate(date);
    }
    setStartDate(date);
  };

  const getLoadMoreLink = () => {
    setCursor((prevState) => prevState + gridItems);
  };

  const computedGridItems = () => {
    return blogItems.slice(0, cursor);
  };

  useEffect(() => {
    const start = () => {
      setIsLoading(true);
    };
    const end = () => {
      setIsLoading(false);
    };
    Router.events.on('routeChangeStart', start);
    Router.events.on('routeChangeComplete', end);
    Router.events.on('routeChangeError', end);
    return () => {
      Router.events.off('routeChangeStart', start);
      Router.events.off('routeChangeComplete', end);
      Router.events.off('routeChangeError', end);
    };
  }, []);

  const onClickSort = (value: string): void => {
    const queryObj = cleanRouterQuery(router.query);
    if (value) {
      queryObj.sort = value;
    }
    const queryString = mapObjectToQueryString(queryObj);
    router.push(`/${lang}${router.asPath.split('?')[0]}${queryString}`, undefined);
  };

  const onClickCat = (value: string): void => {
    const queryObj = cleanRouterQuery(router.query);
    if (value) {
      queryObj.cat = value;
    } else {
      delete queryObj.cat;
    }
    const queryString = mapObjectToQueryString(queryObj);
    router.push(`/${lang}${router.asPath.split('?')[0]}${queryString}`, undefined);
  };

  const isCatSelected = (): string => {
    const queryObj = cleanRouterQuery(router.query);
    const singleQueryCat = Array.isArray(queryObj.cat) ? queryObj.cat[0] : queryObj.cat;
    return singleQueryCat || '';
  };

  const catOptions = !isCourse
    ? [
        {
          label: t('blogNewsCategories.all'),
          value: '',
        },
      ]
    : [];

  if (!isCourse) {
    Object.entries(BlogNewsCategoriesObj).forEach(([key, value]) => {
      catOptions.push({
        label: t(`blogNewsCategories.${key}`),
        value: value,
      });
    });
  }

  const isSortSelected = (): string => {
    const queryObj = cleanRouterQuery(router.query);
    const querySort = queryObj.sort;
    const singleQueryCat = Array.isArray(querySort) ? querySort[0] : querySort;
    return singleQueryCat || '';
  };

  const sortOptions = [
    {
      label: t('sortDateAsc'),
      value: 'asc',
    },
    {
      label: t('sortDateDes'),
      value: 'desc',
    },
  ];

  const rowNumItemsComponent = (
    <ResultsCounterStyled>
      {blogs.length} {isCourse ? t('news') : t('blogs')}
    </ResultsCounterStyled>
  );

  const sortSelectionComponent = (
    <SortGroupStyled>
      {t('sortBy')}
      <CtaDropdown
        options={sortOptions}
        defaultValue={isSortSelected()}
        onSelectCallback={(value) => {
          onClickSort(value);
        }}
      />
    </SortGroupStyled>
  );

  return (
    <>
      <Container>
        <ColumnStyled span={12}>
          <BlogOverviewContentHeader slices={get(data, 'data.body', [])} />
          <RowFilterStyled>
            {!isCourse && (
              <FilterGroupWithSpaceStyled>
                <RowFilterHeadlineStyled>{t('filter')}</RowFilterHeadlineStyled>
                <SortGroupWithSpaceStyled>
                  <CtaDropdown
                    asSelect
                    options={catOptions}
                    defaultValue={isCatSelected()}
                    onSelectCallback={(value) => {
                      onClickCat(value);
                    }}
                  />
                </SortGroupWithSpaceStyled>
              </FilterGroupWithSpaceStyled>
            )}

            <FilterGroupStyled>
              <RowFilterHeadlineStyled>{t('date')}</RowFilterHeadlineStyled>
              <DatePickersGroupStyled>
                <DatePickerStyled
                  startDate={startDate}
                  endDate={endDate}
                  currentDate={startDate}
                  onChange={(date) => onStartDateChange(date)}
                />
                <SpacerStyled>{t('FilterTo')}</SpacerStyled>
                <DatePickerStyled
                  startDate={startDate}
                  endDate={endDate}
                  minDate={startDate}
                  currentDate={endDate}
                  onChange={(date) => onEndDateChange(date)}
                />
              </DatePickersGroupStyled>
            </FilterGroupStyled>
          </RowFilterStyled>
          {isMobile ? (
            <RowSortStyled>
              {sortSelectionComponent}
              {rowNumItemsComponent}
            </RowSortStyled>
          ) : (
            <RowSortStyled>
              {rowNumItemsComponent}
              {sortSelectionComponent}
            </RowSortStyled>
          )}
          <RowGridStyled>
            {computedGridItems().map((item: NewsSliderItemProps, index: number) => (
              <NewsSliderItemStyled
                key={`blog-item-${index}`}
                image={item.image}
                link={item.link}
                subtext={item.subtext}
                text={item.text}
              />
            ))}
          </RowGridStyled>
          <RowLoadingStyled>
            {isloading && <Loading />}
            {!isloading && !blogItems && <div>{t('noResults')}</div>}
            {blogItems.length === 0 && !isloading && <div>{t('noResults')}</div>}
          </RowLoadingStyled>
          {cursor < blogItems.length && (
            <RowPaginationStyled>
              <Link skin="tertiarySmall" onPress={getLoadMoreLink} url={undefined}>
                {t('links.loadMoreBlogEntries')}
              </Link>
            </RowPaginationStyled>
          )}
        </ColumnStyled>
      </Container>

      <Separator primary={{ horizontal_line: true }} />

      <ComponentsRenderer slices={get(data, 'data.body', [])} />
    </>
  );
};
